import { Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import * as Bloodhound from 'corejs-typeahead';
import $ from 'jquery';
import React, { RefObject } from 'react';

import {
	AutocompleteColumnHeader,
	AutocompleteOptions,
	AutocompleteRemoteLoader,
} from '~/components/field/common/autocomplete/autocomplete-react';
import { FieldNameParamType } from '~/utils/acurity-field-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { Logger } from '~/utils/logger';
import { StringUtils } from '~/utils/string-utils';

interface Props {
	id: string;
	items?: any[];
	isLoading?: boolean;
	value: string;
	description: string;
	dataFieldId?: string;
	title?: string;
	remote?: AutocompleteRemoteLoader;
	remoteOptions?: { [option: string]: any | undefined };
	columnName: string;
	columnHeaders?: AutocompleteColumnHeader[];
	listWidth?: number;
	disabled?: boolean;
	displayResultsOnFocus: boolean;
	onValidationError?: (hasError: boolean) => void;
	onSelect?: (record?: { [columnName: string]: any }) => void;
	onSearchStarted?: () => void;
	onSearchCanceled?: () => void;
	onSearchCompleted?: () => void;
	autocomplete: string;
	allowUserInput: boolean;
	handleValueChange?: (value: string) => void;
	handleRefInputElemChange?: (refInputElem: HTMLInputElement) => void;
	entitySearchStyles?: { [type: string]: string };
	resultsDropUp?: boolean;
	fieldTypes?: readonly FieldNameParamType[];
}

interface State {
	value: string;
	isRemoteLoading: boolean;
	hasFocus: boolean;
}

const styles = {
	search__results: 'a-field--search__results',
	search__results__table: 'a-field--search__results__table',
	search__results__suggestion: 'a-field--search__results__suggestion',
	'search__results__suggestion--cursor':
		'a-field--search__results__suggestion--cursor',
	'zindex-container': 'a-field', // 'a-autocomplete__zindex-container',
	'zindex-container--is-open': 'a-autocomplete__zindex-container--is-open',
};

export class Autocomplete extends React.Component<Props, State> {
	private static readonly WILDCARD = '%QUERY';

	/**
	 * Reference to the Autocomplete React Component
	 */
	private readonly reactRefInputEl: RefObject<HTMLInputElement> =
		React.createRef<HTMLInputElement>();
	/**
	 * Reference to the node of above. Accessed from `current` attribute of React ref
	 */
	private inputEl: HTMLInputElement;

	private lastAsyncResult: any[];

	/**
	 * Used by view
	 */
	private readonly hasFocus = false;

	private autocompleteListEl?: HTMLElement;

	private readonly suggestionEngineDefaults: Readonly<
		Bloodhound.BloodhoundOptions<any>
	>;

	private readonly suggestionEngineRemoteDefaults: Readonly<
		Bloodhound.RemoteOptions<any>
	>;

	/**
	 * The max amount of autocomplete items to query for against a remote API
	 */
	private readonly limit: number = 25;

	/**
	 * An arbitrary value of the tolerance distance of the dropdown or dropup list
	 * above/below/to the left of the input element
	 */
	private readonly padding: number = 10;
	/**
	 * The max height from &--search__results max-height: px(400);
	 */
	private readonly maxHeight: number = 400;

	/**
	 * The max amount of autocomplete items to display.
	 *
	 * We want this value to be large enough to show all preloaded FieldModel options
	 * in the customisation editor at once. (Around 100-200 records I think?)
	 *
	 * I chose 10000 as it seemed high enough. Setting the value to 0 makes it default to 5.
	 */
	private readonly maxDisplay: number = 10000;

	private suggestionEngine?: Bloodhound;

	private readonly i18n: I18N = Container.instance.get(I18N);

	constructor(props) {
		super(props);

		this.suggestionEngineDefaults = {
			datumTokenizer: this.onBloodhoundDatumTokenizer,
			queryTokenizer: Bloodhound.tokenizers.whitespace,
			identify: this.onBloodhoundIdentify,
		};
		this.suggestionEngineRemoteDefaults = {
			wildcard: Autocomplete.WILDCARD,
			// NOTE: Jake: 2018-11-14
			// url should be %QUERY% as we utilize it as-is in
			// onBloodhoundTransport below.
			url: '/' + Autocomplete.WILDCARD,
			rateLimitWait: 300,
			replace: this.onBloodhoundReplace,
			transport: this.onBloodhoundTransport,
			// NOTE: Jake: 2019-09-02 - WEBUI-2254
			// We no longer cache as this field can be dependant
			// on other field values.
			cache: false,
		};

		this.state = {
			value: '',
			isRemoteLoading: false,
			hasFocus: false,
		};
	}

	/**
	 * onBloodhoundReplace is a copy-paste of "prepareByWildcard" from typeahead.bundle.js
	 * except we do NOT URL encode the query.
	 */
	private readonly onBloodhoundReplace = (
		url: string,
		query: string,
	): string => {
		// NOTE: Jake: 2019-09-05 - WEBUI-2465
		// By implementing our own "replace" function, we stop Bloodhound from encoding the query
		// string with `encodeURIComponent`.
		return url.replace(Autocomplete.WILDCARD, query);
	};

	private readonly onBloodhoundTransport = (
		bloodhoundOptions: { url: string },
		onBloodhoundSuccess,
		onBloodhoundError,
	): void => {
		const {
			disabled,
			remote,
			remoteOptions,
			displayResultsOnFocus,
			fieldTypes,
		} = this.props;
		if (disabled || !remote) {
			return;
		}
		if (!remote.getResults) {
			throw new Error('Missing remote loader with method "getResults".');
		}
		// NOTE: Jake: 2018-12-04 - WEBUI-1204
		// Create explicit new JS Object {...} as we want the options to not simply
		// reference 'this.remoteOptions'
		const remoteOptionsObject = remoteOptions
			? { ...remoteOptions }
			: undefined;

		// NOTE: Jake: 2018-12-05 - WEBUI-1204
		// Get last part of url so we only have query. At the time of writing, the left-side
		// is simply a unique ID used for caching.
		const url = bloodhoundOptions.url;
		const query = url.substr(url.lastIndexOf('/') + 1);

		const options: AutocompleteOptions = {
			limit: this.limit,
			allowEmptyQuery: displayResultsOnFocus,
			remoteOptions: remoteOptionsObject,
			fieldTypes,
		};
		const promise = remote.getResults(query, options);
		if (promise === undefined) {
			// If user-code is cancelling querying for information, then return
			// without error here.
			return;
		}
		if (!promise) {
			throw new Error(
				remote.constructor.name +
					'::getResults() returned ' +
					typeof promise +
					' instead of the expected promise. Explicitly return "undefined" to cancel the remote call.',
			);
		}

		promise
			.then((data) => {
				onBloodhoundSuccess(data);
			})
			.catch((e) => {
				// NOTE: Jake: 2019-12-06 - WEBUI-2770
				// This was added to handle the case where a user is missing form permissions.
				// ie. Denied access to resource: '/api/personal-details' because user does not have permission to this API for form: 'insurance-options'
				// 	   and customisation: 'null'
				FetchUtils.logAndDisplayError(e);
				onBloodhoundError(e);
				this.lastAsyncResult = undefined;
			});
	};

	private readonly onBloodhoundIdentify = (record: {
		[columnName: string]: any;
	}): string => {
		const { columnName } = this.props;
		const column = record[columnName];
		if (column === undefined) {
			const errorMessage =
				'onBloodhoundIdentify: Cannot find value on column:';
			Logger.warn(errorMessage, columnName, record);
			throw new Error(errorMessage + ' ' + columnName);
		}
		return column;
	};

	/**
	 * This method breaks up a single or multiple column into multiple words that
	 * are used for searching.
	 */
	private readonly onBloodhoundDatumTokenizer = (record: {
		[columnName: string]: any;
	}): string[] => {
		const { columnHeaders } = this.props;
		if (columnHeaders) {
			// Search *every* column in the headers for the value
			const result: string[] = [];
			for (const column of columnHeaders) {
				if (column.hidden) {
					continue;
				}
				const value = record[column.columnName];
				if (value === undefined) {
					continue;
				}
				const tokenizer = column.tokenizer
					? column.tokenizer
					: Bloodhound.tokenizers.nonword;
				result.push(...tokenizer(value));
			}
			return result;
		} else {
			// Search only one column
			const value = this.getInputValueFromRecord(record);
			return Bloodhound.tokenizers.nonword(value);
		}
	};

	/**
	 * Transform a record into a string for use in the <input> field. Known as display value.
	 */
	private getInputValueFromRecord(record: string | number | {}): string {
		const { columnName } = this.props;
		switch (typeof record) {
			case 'object':
				const value: string | number | undefined = record[columnName];
				if (value === undefined) {
					throw new Error(
						'getInputValueFromRecord: Cannot find value on column: ' +
							columnName,
					);
				}
				return String(value);
			case 'undefined':
				return '';
			default:
				return String(record);
		}
	}

	/**
	 * Analogous to Aurelia's attached() method
	 */
	public componentDidMount(): void {
		const { value, handleRefInputElemChange } = this.props;
		this.inputEl = this.reactRefInputEl.current;
		if (!this.inputEl) {
			throw new Error(
				'Missing <input> for Autocomplete. Has the "ref" attribute been removed?',
			);
		}

		// NOTE: Jake: 2018-12-13 - WEBUI-1304
		// This must happen before Typeahead initializes
		const wasFocused = document.activeElement === this.inputEl;

		// NOTE: Jake: 2018-12-03 - WEBUI-1255
		// This must be bound before `typeahead` is initialized.
		$(this.inputEl).on('keydown', this.onKeydownBeforeTypeahead);

		// NOTE: Jake: 2019-02-07
		// Stop "undefined" appearing in autocomplete fields for a brief second
		// when opening modals.
		// this.inputEl.value = this.value !== undefined ? this.getValueAndDescriptionString() : '';
		this.inputEl.value = value !== undefined ? String(value) : '';

		// Typeahead
		$(this.inputEl).typeahead(
			{
				hint: false,
				highlight: true,
				minLength: 0,
				classNames: {
					menu: styles.search__results + ' tt-menu dropdown-menu',
					cursor: styles['search__results__suggestion--cursor'],
					dataset: styles.search__results__table,
				},
			},
			{
				limit: this.maxDisplay,
				source: this.onTypeaheadSource,
				display: this.onTypeaheadDisplay,
				templates: {
					header: this.onTypeaheadTemplateHeader,
					suggestion: this.onTypeaheadTemplateSuggestion,
					notFound: this.onTypeaheadTemplateNotFound,
					// Pending means notFound for local/synchronous search, as we are never waiting for remote for local.
					pending: this.hasPrefetchedItems()
						? this.onTypeaheadTemplatePreFetchNotFound
						: undefined,
				},
			},
		);

		$(this.inputEl).on('typeahead:open', (e: JQuery.Event) =>
			this.onTypeaheadOpened(),
		);
		$(this.inputEl).on('typeahead:close', (e: JQuery.Event) =>
			this.onTypeaheadClosed(),
		);
		$(this.inputEl).on('typeahead:render', (e: JQuery.Event) =>
			this.onTypeaheadRender(),
		);
		$(this.inputEl).on(
			'typeahead:select',
			(
				e: JQuery.Event,
				record: { [columnName: string]: any },
				index: string,
			) => this.onTypeaheadSelect(record),
		);
		this.inputEl.addEventListener('change', this.onInputChange);

		$(this.inputEl).on(
			'typeahead:asyncrequest',
			this.onTypeaheadAsyncRequest,
		);
		$(this.inputEl).on(
			'typeahead:asynccancel',
			this.onTypeaheadAsyncCancel,
		);
		$(this.inputEl).on(
			'typeahead:asyncreceive',
			this.onTypeaheadAsyncReceive,
		);

		// Storing a reference to the last retrieved async result
		// There is not an event for when the bloodhound cache is updated, but the list is available in the render event.
		$(this.inputEl).on(
			'typeahead:render',
			(e: JQuery.Event, suggestions: any, isAsync: boolean) => {
				if (isAsync) {
					this.lastAsyncResult = suggestions;
				}
			},
		);

		this.initSuggestionEngine();

		this.autocompleteListEl = this.inputEl.parentElement.querySelector(
			'.' + styles.search__results,
		);
		if (!this.autocompleteListEl) {
			throw new Error('Unable to find autocompleteListEl.');
		}

		// NOTE: Jake: 2018-12-13 - WEBUI-1304
		// Workaround Typeahead bug where it won't open if you focused
		// the input before Typeahead initialized. So if we were focused
		// on this field, let's force it open.
		if (wasFocused) {
			$(this.inputEl).typeahead('open');
		}

		// NOTE: Jake: 2019-05-22 - WEBUI-2004
		// Feed input element up the bind chain so that <field-holder>
		// can apply focus/defocus events to it.
		if (handleRefInputElemChange) {
			handleRefInputElemChange(this.inputEl);
		}
	}

	public componentWillUnmount(): void {
		$(this.inputEl).off('keydown');
		$(this.inputEl).off('typeahead:select');
		$(this.inputEl).off('typeahead:asyncreceive');
		this.inputEl.removeEventListener('change', this.onInputChange);
		$(this.inputEl).typeahead('destroy');
		this.autocompleteListEl = undefined;
		this.suggestionEngine = undefined;
	}

	private readonly onKeydownBeforeTypeahead = (e: JQuery.Event): void => {
		if (e.key === 'Tab') {
			// NOTE(Jake): 2018-12-03 - WEBUI-1255
			// Typeahead default behaviour is to select the first autocomplete item
			// from a list when you Tab. To disable this, I force the autocomplete list
			// closed before the Typeahead keydown event fires, this makes tabbing naturally
			// jump to the next element.
			$(this.inputEl).typeahead('close');
		}
	};

	private readonly onTypeaheadSource = (
		q: string,
		syncResults: (result: Array<{}>) => void,
		asyncResults?: (result: Array<{}>) => void,
	): void => {
		const { value, displayResultsOnFocus } = this.props;
		const query = q.trim();
		if (
			this.hasPrefetchedItems() &&
			(!query || value === query) &&
			displayResultsOnFocus
		) {
			// NOTE: Jake: 2019-06-13 - WEBUI-1204
			// Open all autocomplete results if they're preloaded if:
			// - Blank query
			// - Query is the same as the value. (ie. show full list if clicking on field that was filled out previously)
			Logger.log('Displaying preloaded items');
			syncResults(this.suggestionEngine.all());
		} else if (query || displayResultsOnFocus) {
			Logger.log(`Searching: "${query}"`);
			return this.suggestionEngine.search(
				query,
				syncResults,
				asyncResults,
			);
		} else {
			Logger.log(`Searching a: "${query}"`);
		}
	};

	/**
	 * This is the value that will be put into the <input> field when you select
	 * the autocomplete suggestion.
	 */
	private readonly onTypeaheadDisplay = (record: {
		[columnName: string]: any;
	}): string => {
		return this.getInputValueFromRecord(record);
	};

	private readonly onTypeaheadTemplateHeader = (): string => {
		const { columnHeaders } = this.props;
		let columnsMarkup: string = '<div></div>';
		if (columnHeaders && columnHeaders.length > 0) {
			columnsMarkup = '';
			for (const column of columnHeaders) {
				if (column.hidden) {
					continue;
				}
				columnsMarkup += `<div>${column.display}</div>`;
			}
		}
		return `
			<div class="a-field--search__results__header row">
				${columnsMarkup}
			</div>
		`;
	};

	/**
	 * The markup used to show suggestions as you type.
	 */
	private readonly onTypeaheadTemplateSuggestion = (suggestion: {
		[columnName: string]: any;
		_query: string;
	}): string => {
		const { columnHeaders, columnName } = this.props;

		let columnsMarkup: string = '';
		if (columnHeaders) {
			for (const columnHeader of columnHeaders) {
				if (columnHeader.hidden) {
					continue;
				}
				let column = suggestion[columnHeader.columnName];
				if (column === undefined) {
					// NOTE(Jake): 2018-12-04 - WEBUI-1204
					// This codepath executes due to aWeird timing issue with using two different autocomplete fields or something?
					// For now, lets make the column default to a blank string in this error case.
					// I think the searchDefinition / columnHeaders must be changing while waiting for a request, so it tries to use
					// new column headers with an old request.
					//
					// NOTE(Jae): 2020-05-01 - WEBUI-3196
					// This codepath is hit when selecting a FieldModel option on the customisation editor for the Family Law Split Details
					// form. So I changed this log message from "error" to "warn".
					column = '';
					Logger.warn(
						'onTypeaheadTemplateSuggestion: Cannot find column: ' +
							columnHeader.columnName +
							' on autocomplete suggestion. Using empty value.',
						suggestion,
					);
				}
				if (columnHeader.formatter) {
					column = columnHeader.formatter(column);
					if (column === undefined) {
						throw new Error(
							'onTypeaheadTemplateSuggestion: "formatter" provided for ' +
								columnHeader.columnName +
								' is returning undefined. This is not allowed. At least return an empty string.',
						);
					}
				}

				if (columnHeader.render) {
					column = columnHeader.render(suggestion);
				}

				columnsMarkup += `<div>${this.escapeHtml(column)}</div>`;
			}
		} else {
			const column = suggestion[columnName];
			if (column === undefined) {
				Logger.warn('onTypeaheadTemplateSuggestion', suggestion);
				throw new Error(
					'onTypeaheadTemplateSuggestion: Cannot find value on column: ' +
						columnName,
				);
			}
			columnsMarkup = `
				<div>${this.escapeHtml(column)}</div>
			`;
		}

		// Check if record is deleted
		let isDeleted: boolean = false;
		if (suggestion) {
			for (const fieldName in suggestion) {
				if (!suggestion.hasOwnProperty(fieldName)) {
					continue;
				}
				if (
					FormModelUtils.isDeletedField(fieldName) &&
					suggestion[fieldName] === 'Y'
				) {
					isDeleted = true;
					break;
				}
			}
			// Check if member record is exited
			const memberStatusValue =
				suggestion[FormModelUtils.MemberAccountStatusField];
			if (
				memberStatusValue !== undefined &&
				!FormModelUtils.isMemberAccountStatusActive(memberStatusValue)
			) {
				isDeleted = true;
			}
		}

		// Set classes for row
		const rowClasses = ['a-field--search__results__suggestion', 'row'];
		if (isDeleted) {
			// NOTE: Jae: 2020-01-14 - WEBUI-2790
			// We highlight deleted fields just like the original TreeView app.
			rowClasses.push('a-field--search__results__suggestion--is-deleted');
		}

		return (
			`
			<div class="` +
			rowClasses.join(' ') +
			`">
				${columnsMarkup}
			</div>
		`
		);
	};

	private readonly onTypeaheadTemplateNotFound = (): string => {
		const classNames = [
			'a-field--search__results__suggestion',
			'a-field--search__results__suggestion--empty',
			'row',
			'tt-suggestion',
		].join(' ');
		const notFoundText = this.i18n.tr('acurity-search.no-result-found');
		return `
			<div class="${classNames}">
				<div class="colspan">
					${notFoundText}
				</div>
			</div>
		`;
	};

	/**
	 * Compared to `onTypeaheadTemplateNotFound`, this method generates template with a different class
	 * `a-field--search__results__suggestion--prefetch-empty` but shows the same `not found text`. So that
	 * in cypress test, a search field that is waiting for result from remote can still do a wait action
	 * on element with class `a-field--search__results__suggestion--empty`
	 */
	private readonly onTypeaheadTemplatePreFetchNotFound = (): string => {
		const classNames = [
			'a-field--search__results__suggestion',
			'a-field--search__results__suggestion--prefetch-empty',
			'row',
			'tt-suggestion',
		].join(' ');
		const notFoundText = this.i18n.tr('acurity-search.no-result-found');
		return `
			<div class="${classNames}">
				<div class="colspan">
					${notFoundText}
				</div>
			</div>
		`;
	};

	private hasPrefetchedItems(): boolean {
		const { items } = this.props;
		return items !== undefined && items !== null;
	}

	private onTypeaheadOpened(): void {
		// NOTE (BEN): WEBUI-4782
		// Somehow this.typeAheadRerenderNeeded() behave differently in QA environment and local prod environment
		// that causes autocomplete does not perform update datasets
		if (this.typeAheadRerenderNeeded()) {
			Autocomplete.updateTypeAheadDataSets(this.inputEl);
		}
		const doc = this.inputEl.ownerDocument;
		doc.body
			.querySelectorAll(`.${styles['zindex-container']}`)
			.forEach((el) => {
				if (el.contains(this.inputEl)) {
					el.classList.add(styles['zindex-container--is-open']);
				}
			});
	}

	private onTypeaheadClosed(): void {
		const doc = this.inputEl.ownerDocument;
		doc.body
			.querySelectorAll(`.${styles['zindex-container']}`)
			.forEach((el) => {
				if (el.contains(this.inputEl)) {
					el.classList.remove(styles['zindex-container--is-open']);
				}
			});
	}

	private onTypeaheadRender(): void {
		const { listWidth, resultsDropUp } = this.props;
		const autocompleteListEl: HTMLElement = this.autocompleteListEl;
		// NOTE: Jake: 2019-05-02 - WEBUI-1697
		// Using `cypress run` can inexplicably fail with "Cypress detected that an uncaught error was thrown from a cross origin script."
		// The real issue is that `autocompleteListEl.parentNode` is becoming undefined and then this callback is firing and trying to use it.
		// My assumption is that Cypress is just guessing heuristically that the issue is related to cross origin script issues.
		//
		// Regardless of Cypress, we probably want this "parentNode" check anyway as the entire DOM could disappear when someone navigates between
		// pages, which would cause parentNode to be undefined and therefore crash.
		if (autocompleteListEl && autocompleteListEl.parentNode) {
			// NOTE: Jake: 2019-04-09 - WEBUI-1697
			// Draw the autocomplete relative to the screen rather than within
			// the scope of the element its in. This is so it can overflow outside of
			// the slideout and modals.
			{
				// Get closest "fixed" element or <body>
				let contextEl = autocompleteListEl.parentNode as HTMLElement;
				while (contextEl && contextEl.tagName !== 'BODY') {
					const computedStyle = getComputedStyle(contextEl);
					if (computedStyle.position === 'fixed') {
						break;
					}
					contextEl = contextEl.parentNode as HTMLElement;
				}
				if (contextEl && contextEl.tagName === 'BODY') {
					contextEl = document.documentElement;
				}

				// Append to <body> or <ux-dialog-container>
				{
					const inputRect = this.inputEl.getBoundingClientRect();
					const scrollLeft = contextEl.scrollLeft;
					const scrollTop = contextEl.scrollTop;
					const left = inputRect.left + scrollLeft;
					const top = inputRect.top + inputRect.height + scrollTop;
					const middle = contextEl.getBoundingClientRect().height / 2;

					// NOTE: Jake: 2019-04-09
					// Calculate the distance between bottom of the screen and input
					// and make sure the autocomplete list never draws off-screen.
					{
						const contextBottom =
							contextEl.getBoundingClientRect().bottom;
						const maxHeightDown =
							contextBottom - inputRect.bottom - this.padding;
						const maxHeightUp =
							inputRect.top - this.padding > this.maxHeight
								? this.maxHeight
								: inputRect.top - this.padding;
						// this is the bottom position of search results, when running e2e test, the contextEl
						// cannot be calculated correctly so the position might be negative when running tests.
						// this will fail some of the test cases.
						const topupBottom = contextBottom - inputRect.top;
						// if element is below half of the window element make it Dropup and ensure
						// topupBottom will be positive number.
						if (
							resultsDropUp ||
							(topupBottom > 0 && top > middle)
						) {
							autocompleteListEl.style.top = 'unset';
							autocompleteListEl.style.bottom =
								topupBottom + 'px';
							autocompleteListEl.style.maxHeight =
								maxHeightUp + 'px';
						} else {
							// SR-23422 the style of the list element persist between each re-rendering,
							// the bottom property from above clause will still exist at below clause.
							// Because the element has maxHeight property, the co-existence of bottom and top
							// property will squeeze the list element to be very small
							autocompleteListEl.style.bottom = 'unset';
							autocompleteListEl.style.maxHeight =
								maxHeightDown + 'px';
							autocompleteListEl.style.top = top + 'px';
						}
					}

					// NOTE: Jake: 2019-04-09
					// Need to use 'fixed' to draw in-front of modals
					autocompleteListEl.style.position = 'fixed';
					autocompleteListEl.style.left = left + 'px';
					autocompleteListEl.style.width = inputRect.width + 'px';

					// NOTE: Jake: 2019-04-09
					// Need to append to the body so 'fixed' works inside modal.
					document.body.appendChild(autocompleteListEl);
				}
			}

			if (listWidth) {
				autocompleteListEl.style.width = listWidth + 'px';
			}
		}
	}

	private onTypeaheadSelect(record: { [columnName: string]: any }): void {
		this.setValueWithTriggers(record);
	}

	// if the typeahead results position is out by >10px around the inputElement when the
	// window is scrolled or resized, or when using search fields within the filter panel,
	// or when displayResultOnFocus is set to true, then the typeahead needs to be rerendered
	private typeAheadRerenderNeeded(): boolean {
		if (this.props.displayResultsOnFocus) return true;
		const autocompleteListEl: HTMLElement = this.autocompleteListEl;
		if (autocompleteListEl && autocompleteListEl.parentNode) {
			const heightAutocompleteListEl =
				autocompleteListEl.getBoundingClientRect().height;
			if (heightAutocompleteListEl === 0) {
				return false;
			}
			const contextEl = autocompleteListEl.parentNode as HTMLElement;
			const inputRect = this.inputEl.getBoundingClientRect();
			const scrollTop = contextEl.scrollTop;
			const top = inputRect.top + scrollTop;
			const bottom = inputRect.bottom + scrollTop;
			const topAutocompleteListEl =
				autocompleteListEl.getBoundingClientRect().top;
			const bottomAutocompleteListEl =
				autocompleteListEl.getBoundingClientRect().bottom;

			// NOTE(BEN) WEBUI-4782
			// top and bottomAutocompleteListEl somehow have different value in QA and local environment
			// in local, top - bottomeAutoCompleteListEl < 0 will always be falcy while not in QA
			// This causes dataset never be updated in QA env.
			if (
				topAutocompleteListEl < bottom &&
				(top - bottomAutocompleteListEl < 0 ||
					top - bottomAutocompleteListEl > this.padding)
			) {
				return true;
			}
			if (
				topAutocompleteListEl > bottom &&
				(topAutocompleteListEl - bottom < 0 ||
					topAutocompleteListEl - bottom > this.padding)
			) {
				return true;
			}

			// WEBUI-4309: ensure results align horizontally with input
			// fixes misaligned empty results on filter panel search fields
			const left = inputRect.left;
			const leftAutocompleteListEl =
				autocompleteListEl.getBoundingClientRect().left;
			if (
				leftAutocompleteListEl < 0 &&
				(leftAutocompleteListEl - left < 0 ||
					leftAutocompleteListEl - left > this.padding)
			) {
				return true;
			}
		}
		return false;
	}

	private readonly onInputChange = (e: Event): void => {
		const { value, onValidationError } = this.props;
		const { isRemoteLoading } = this.state;
		const inputEl: HTMLInputElement = e.currentTarget as HTMLInputElement;
		const inputFieldValue = inputEl.value;
		if (!inputFieldValue) {
			// NOTE: Jake: 2019-07-22 - WEBUI-2278
			// We used to just set this as "this.value = undefined" but because
			// "undefined" values aren't sent to the server, it didn't work.
			// We now set "this.value = ''" if the value is truthy.
			// NOTE(Jae): 2020-03-03 - WEBUI-3122
			// We need this to fire regardless of "isRemoteLoading" otherwise
			// the address.test.ts test fails with the Suburb field, you can't clear
			// the field if it's still waiting for a response, which makes no sense anyway.
			this.clearValueIfTruthy();
			return;
		}
		if (value === inputFieldValue) {
			// NOTE: Jake: 2018-11-12 - WEBUI-1122
			// If there are two or more autocomplete items
			// with the same value and you select one from the dropdown, this
			// if-statement will prevent the value from just using the first
			// autocomplete item in the list.

			return;
		}
		if (!isRemoteLoading) {
			// NOTE(Jae): 2020-03-03 - WEBUI-3122
			// If not loading from a remote, execute immediately.
			this.handleInputChange(inputEl);
			return;
		}
		// NOTE(Jae): 2020-03-03
		// Emrys's note was: "we don't want to try and validate the input while a request is in progress".
		// So if we're waiting on the remote, only fire this event after we receive the response.
		$(inputEl).one('typeahead:asyncreceive', () => {
			this.handleInputChange(inputEl);
		});
	};

	/**
	 * Set the value using the "record" from the autocomplete list, also trigger
	 * the callback to provide the full autocomplete record.
	 */
	private setValueWithTriggers(record: { [columnName: string]: any }): void {
		const { value, handleValueChange, onSelect, onValidationError } =
			this.props;
		const newValue = this.getInputValueFromRecord(record);
		// NOTE: Jake: 2019-01-24 - WEBUI-1262
		// We check if the value changed so that onSelect() callback can safely
		// blank out other fields.
		// ie. if you change the "State" from "VIC" to "NSW", then empty out the
		// 	   other address fields.
		if (value !== newValue) {
			// after select a valid report, clear the error
			if (onValidationError) {
				onValidationError(false);
			}
			this.setValue(newValue, handleValueChange);
			if (onSelect) {
				onSelect(record);
			}
		}
	}

	private clearValueIfTruthy = (): void => {
		const { value, handleValueChange, onSelect } = this.props;
		if (!value) {
			// Do not trigger onSelect callback
			// if value is already falsey.
			return;
		}
		this.setValue('', handleValueChange);
		if (onSelect) {
			onSelect(undefined);
		}
	};

	private readonly handleInputChange = (inputEl: HTMLInputElement): void => {
		const { value, onValidationError, handleValueChange } = this.props;
		const inputFieldValue = inputEl.value;
		const newRecord =
			this.findFirstMatchInAutocompleteList(inputFieldValue);
		if (newRecord === undefined) {
			if (this.mustRequireSelectFromList()) {
				$(inputEl).typeahead('val', value);
				if (onValidationError) {
					onValidationError(true);
				}
				// Set blank value as the inputFieldValue is not valid.
				// Clear the record after set to '',disable the Start button
				this.clearValueIfTruthy();
			} else {
				// If a user doesn't need to select an autocomplete item, just set
				// to the raw string
				this.setValue(inputFieldValue, handleValueChange);
			}
			return;
		}
		this.setValueWithTriggers(newRecord);
	};

	private findFirstMatchInAutocompleteList(
		valueToSearchWith: string,
	): { [fieldName: string]: any } | undefined {
		const { columnName } = this.props;
		let newRecord: { [fieldName: string]: any } | undefined;
		const items = this.getAutocompleteList();
		let matchingCount = 0;
		if (items && items.length > 0) {
			for (const record of items) {
				const value: string | number | undefined = record[columnName];
				if (
					value !== undefined &&
					String(value) === valueToSearchWith
				) {
					if (!newRecord) {
						newRecord = record;
					}
					matchingCount++;
				}
			}
		}
		if (DEVELOPMENT && matchingCount > 1) {
			Logger.warn(
				'Autocomplete',
				'Multiple matches with autocomplete data, choosing first found record.',
			);
		}
		return newRecord;
	}

	private mustRequireSelectFromList(): boolean {
		const { allowUserInput, remote } = this.props;
		return (
			!allowUserInput && (Boolean(remote) || this.hasPrefetchedItems())
		);
	}

	private readonly getAutocompleteList = (): any[] => {
		const { remote, items } = this.props;
		let autoCompleteList = [];
		if (remote && this.lastAsyncResult) {
			autoCompleteList = this.lastAsyncResult;
		} else if (items && items.length > 0) {
			autoCompleteList = items;
		}
		return autoCompleteList;
	};

	private readonly onTypeaheadAsyncRequest = (
		e: JQuery.Event,
		unused,
		searchString: string,
	): void => {
		const { disabled, remote, displayResultsOnFocus, onSearchStarted } =
			this.props;
		if (!disabled && remote && (searchString || displayResultsOnFocus)) {
			// async events can be fired when remote is disabled
			this.setState({ isRemoteLoading: true });
			if (onSearchStarted) {
				onSearchStarted();
			}
		}
	};

	private readonly onTypeaheadAsyncCancel = (): void => {
		const { onSearchCanceled } = this.props;
		this.setState({ isRemoteLoading: false });
		if (onSearchCanceled) {
			onSearchCanceled();
		}
	};

	private readonly onTypeaheadAsyncReceive = (): void => {
		const { onSearchCompleted } = this.props;
		this.setState({ isRemoteLoading: false });
		if (onSearchCompleted) {
			onSearchCompleted();
		}
	};

	private readonly initSuggestionEngine = (): void => {
		const { remote, items, disabled } = this.props;
		if (DEVELOPMENT && this.suggestionEngine) {
			throw new Error('Cannot initialize suggestion engine twice.');
		}
		const config = { ...this.suggestionEngineDefaults };
		if (remote) {
			config.remote = { ...this.suggestionEngineRemoteDefaults };
		}
		if (!remote && this.hasPrefetchedItems()) {
			config.local = [];
			if (items.length > 0) {
				config.local = items;
			}
		}
		if (!disabled && (config.remote || config.local)) {
			$(this.inputEl).typeahead('enable');
		} else {
			// NOTE: Jake: 2018-11-16
			// If the "items" / "remote" passed in become undefined or
			// disabled, then the autocomplete behaviour should turn off as well.
			// This pattern is utilized by "suburb.ts", "state.ts" and "postcode.ts"
			$(this.inputEl).typeahead('disable');
		}
		this.suggestionEngine = new Bloodhound(config);
	};

	/**
	 * Used by view
	 */
	private onClickClearButton = (e): void => {
		const { value } = this.props;
		if (!value || (this.inputEl && this.inputEl.disabled)) {
			return;
		}
		this.clearValueIfTruthy();
		if (document.activeElement !== this.inputEl) {
			this.inputEl.focus();
		}
	};

	private readonly onFocus = (): void => {
		this.setState({ hasFocus: true });
	};

	private readonly onBlur = (): void => {
		this.setState({ hasFocus: false });
	};

	/**
	 * Helper method used to update `value` within the local state and to trigger the
	 * `handleValueChange` callback that is used to lift `value` up the bindable chain
	 */
	private setValue(
		value: string,
		handleValueChange?: (value: string) => void,
	) {
		this.setState(
			{ value },
			handleValueChange ? () => handleValueChange(value) : undefined,
		);
	}

	public componentDidUpdate(prevProps: Props) {
		const { value, items, remote } = this.props;
		if (prevProps.value !== value) {
			this.valueChanged(value);
		} else if (prevProps.items !== items) {
			this.itemsChanged(items);
		} else if (prevProps.remote !== remote) {
			this.remoteChanged(remote);
		}
	}

	private valueChanged(newValue: string) {
		if (!this.suggestionEngine || !this.inputEl) {
			return;
		}

		$(this.inputEl).typeahead('val', newValue);
	}

	private itemsChanged(newItems: any[]) {
		const { remote, handleValueChange, onValidationError } = this.props;
		const { value } = this.state;

		if (!this.isAttached()) {
			return;
		}
		if (remote && this.hasPrefetchedItems()) {
			throw new Error(
				'Cannot use "remote" and "items" properties at the same time.',
			);
		}

		// Auto-validate value based on list
		if (
			this.mustRequireSelectFromList() &&
			value &&
			newItems &&
			newItems.length > 0
		) {
			const newRecord = this.findFirstMatchInAutocompleteList(value);
			if (newRecord !== undefined) {
				this.setValue(
					this.getInputValueFromRecord(newRecord),
					handleValueChange,
				);
				if (onValidationError) {
					onValidationError(false);
				}
			} else {
				this.setValue('', handleValueChange);
				if (onValidationError) {
					onValidationError(true);
				}
			}
		}

		// Re-init
		this.suggestionEngine = undefined;
		this.initSuggestionEngine();

		// hack to update typeahead datasets dynamically
		Autocomplete.updateTypeAheadDataSets(this.inputEl);
	}

	private remoteChanged(remote: AutocompleteRemoteLoader) {
		if (!this.isAttached()) {
			return;
		}
		if (remote && this.hasPrefetchedItems()) {
			throw new Error(
				'Cannot use "remote" property and "items" at the same time.',
			);
		}
		if (remote && !remote.getResults) {
			throw new Error('Missing remote loader with method "getResults".');
		}

		// Re-init
		this.suggestionEngine = undefined;
		this.initSuggestionEngine();
	}

	private isAttached(): boolean {
		return (
			this.inputEl &&
			this.inputEl.parentNode &&
			this.suggestionEngine instanceof Bloodhound
		);
	}

	// hack to update typeahead datasets dynamically
	// https://github.com/twitter/typeahead.js/issues/1644
	public static updateTypeAheadDataSets(
		inputElem: HTMLInputElement | HTMLSelectElement,
	) {
		if (!inputElem) {
			throw new Error('Missing input element');
		}
		const inputElemJquery = $(inputElem);
		if (!inputElemJquery.typeahead) {
			throw new Error('Typeahead is not initialised on input element');
		}

		const oldVal = inputElemJquery.val() as string;
		inputElemJquery.typeahead('val', 'zzz');
		inputElemJquery.typeahead('val', oldVal);
	}

	public render() {
		const {
			isLoading,
			remote,
			autocomplete,
			id,
			dataFieldId,
			disabled,
			title,
			description,
			value,
			entitySearchStyles,
		} = this.props;
		const { isRemoteLoading, hasFocus } = this.state;

		const classNames: string[] = [
			'a-field__input__input',
			'a-autocomplete__input',
		];
		if (entitySearchStyles) {
			classNames.push(
				entitySearchStyles.input,
				entitySearchStyles.autocomplete,
			);
		}

		const hasLoadingIcon = isLoading || isRemoteLoading;
		const hasSearchIcon =
			!hasLoadingIcon &&
			(this.hasPrefetchedItems() ||
				(remote !== undefined && remote !== null));
		return (
			<div className="a-autocomplete__root">
				<input
					type="text"
					className={classNames.join(' ')}
					autoComplete={autocomplete}
					id={id}
					name={id}
					data-field-id={dataFieldId || id}
					ref={this.reactRefInputEl}
					disabled={disabled}
					title={title || ''}
					onFocus={this.onFocus}
					onBlur={this.onBlur}
				/>
				{!hasFocus && (
					<div className="a-autocomplete__description-overlay">
						<span className="a-autocomplete__description-overlay__value">
							{value}
						</span>
						<span className="a-autocomplete__description-overlay__readonly-description">
							{description}
						</span>
					</div>
				)}
				{value && hasSearchIcon && !hasLoadingIcon && (
					<button
						type="button"
						className="a-autocomplete__button"
						onClick={(e) => this.onClickClearButton(e)}
						tabIndex={-1}
						data-skipendfieldonblur
					>
						<i
							className="a-autocomplete__icon__relative fas fa-times"
							aria-hidden="true"
						/>
						<span className="sr-only">Clear</span>
					</button>
				)}
				{!value && hasSearchIcon && (
					<i
						className="a-autocomplete__icon fas fa-search"
						aria-hidden="true"
					/>
				)}
				{hasLoadingIcon && (
					<i
						className="a-autocomplete__icon fas fa-circle-notch fa-spin"
						aria-hidden="true"
					/>
				)}
			</div>
		);
	}

	private escapeHtml(obj: any): string {
		return StringUtils.escapeHtml(obj.toString());
	}
}
